// The andon live stream, from the BFF.
//
// Ported from the Andon app's `useLive`. One EventSource for the whole app,
// opened once there is a session and the andon preview is on. What comes down
// it is a nudge and not a replica: "alert 900 changed", "alert 900 has a
// message". The screens re-read through the queries they already use, so
// there is one shape of an andon in this client rather than two that have to
// be kept in step.
//
// WHY IT DOES NOT REPLACE THE POLL
//
// The hub is in-memory in one process. A second instance of the BFF does not
// see what this one publishes, a reconnect loses whatever was sent while the
// socket was down, and a phone that slept missed everything. So the polls the
// andon queries already run stay as the backstop; the stream only makes the
// common case instant.
//
// EventSource reconnects by itself, with the delay the server sends in its
// `retry:` field, so there is no reconnect loop here to get wrong.

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@/store';

type LiveEvent = { kind: 'alert' | 'message'; alertId: number };

/**
 * How long a nudge waits for the ones behind it.
 *
 * A busy floor changes several alerts inside a sweep tick, and each of those
 * is an event to every device. Without this, one tick would cost each device
 * one re-read per changed alert — more traffic than the poll this helps, in
 * the exact moment it can least afford it.
 */
const COALESCE_MS = 400;

export function useAndonLive(enabled: boolean): void {
  const qc = useQueryClient();
  const setLive = useStore((s) => s.setLive);
  // Held in a ref, not state: a pending flush must survive re-renders without
  // scheduling a second one.
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    const flush = () => {
      timer.current = null;
      // Every andon read in the app is keyed under 'andons', whatever its
      // scope — the station's, the unanswered, one by id — so one
      // invalidation reaches whichever screen happens to be mounted.
      void qc.invalidateQueries({ queryKey: ['andons'] });
    };

    const schedule = () => {
      if (timer.current == null) timer.current = window.setTimeout(flush, COALESCE_MS);
    };

    const source = new EventSource('/api/andon/live', { withCredentials: true });
    const onEvent = (e: MessageEvent) => {
      try {
        const event = JSON.parse(e.data) as LiveEvent;
        if (Number.isInteger(event?.alertId)) schedule();
      } catch {
        // A frame this build does not understand is not a reason to tear the
        // stream down: a newer server may send a kind this client has never
        // heard of, and the poll covers whatever it meant.
      }
    };

    source.addEventListener('alert', onEvent);
    source.addEventListener('message', onEvent);
    source.addEventListener('open', () => setLive(true));
    // EventSource reports a drop and then reconnects on its own. Saying so
    // matters because the polls are the whole of the coverage while it is down.
    source.addEventListener('error', () => setLive(false));

    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
      timer.current = null;
      source.close();
      setLive(false);
    };
  }, [enabled, qc, setLive]);
}

/**
 * A poll interval that knows whether the stream is up.
 *
 * `fast` is the interval that has to be right when nothing is being pushed;
 * `slow` is the backstop behind a working stream, which only has to catch what
 * a second BFF instance published or what a reconnect missed.
 */
export function useLiveInterval(fast: number, slow: number): number {
  return useStore((s) => (s.live ? slow : fast));
}
